import * as React from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAllPaymentPlans, useHasActivePaymentPlan, usePaymentStatusReport } from "@/hooks/useAccounting";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import { getPlanStatusBadgeClass } from "@/lib/status-tag-colors";

export function PaymentPlansTab() {
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const plans = useAllPaymentPlans(statusFilter !== "all" ? statusFilter : undefined);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [markPaidPlan, setMarkPaidPlan] = React.useState<any>(null);

  const markDefaulted = useMutation(api.paymentPlans.markPlanDefaulted);
  const cancelPlan = useMutation(api.paymentPlans.cancelPaymentPlan);

  const filteredPlans = React.useMemo(() => {
    if (!plans) return [];
    if (!search) return plans;
    const q = search.toLowerCase();
    return plans.filter(
      (p: any) =>
        p.customerName?.toLowerCase().includes(q) ||
        p.customerEmail?.toLowerCase().includes(q),
    );
  }, [plans, search]);

  const stats = React.useMemo(() => {
    if (!plans) return { active: 0, outstanding: 0, overdue: 0 };
    const active = plans.filter((p: any) => p.status === "active");
    return {
      active: active.length,
      outstanding: active.reduce((s: number, p: any) => s + ((p.totalAmount ?? 0) - (p.paidAmount ?? 0)), 0),
      overdue: active.reduce((s: number, p: any) => s + (p.overdueCount ?? 0), 0),
    };
  }, [plans]);

  const handleMarkDefaulted = React.useCallback(
    async (planId: string) => {
      const reason = window.prompt("Reason for marking as defaulted:");
      if (!reason) return;
      try {
        await markDefaulted({ paymentPlanId: planId as any, reason });
        toast.success("Plan marked as defaulted");
      } catch (err) {
        toast.error(
          toUserFacingErrorMessage(err, "Couldn't mark plan as defaulted. Please try again."),
        );
      }
    },
    [markDefaulted],
  );

  const handleCancel = React.useCallback(
    async (planId: string) => {
      const reason = window.prompt("Reason for cancellation (optional):");
      try {
        await cancelPlan({ paymentPlanId: planId as any, reason: reason || undefined });
        toast.success("Plan cancelled");
      } catch (err) {
        toast.error(
          toUserFacingErrorMessage(err, "Couldn't cancel plan. Please try again."),
        );
      }
    },
    [cancelPlan],
  );

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Active Plans</p>
            <p className="text-2xl font-bold">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Outstanding</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.outstanding)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Overdue Installments</p>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Plans</CardTitle>
              <CardDescription>Manage payment arrangements</CardDescription>
            </div>
            <Button onClick={() => setCreateOpen(true)}>Create Plan</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              placeholder="Search customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="defaulted">Defaulted</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredPlans.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No payment plans found
            </p>
          ) : (
            <div className="space-y-2">
              {filteredPlans.map((plan: any) => (
                <PlanRow
                  key={plan._id}
                  plan={plan}
                  onMarkPaid={() => setMarkPaidPlan(plan)}
                  onMarkDefaulted={() => void handleMarkDefaulted(plan._id)}
                  onCancel={() => void handleCancel(plan._id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {createOpen ? (
        <CreatePaymentPlanDialog onClose={() => setCreateOpen(false)} />
      ) : null}
      {markPaidPlan ? (
        <MarkPaidDialog
          plan={markPaidPlan}
          onClose={() => setMarkPaidPlan(null)}
        />
      ) : null}
    </div>
  );
}

function PlanRow({
  plan,
  onMarkPaid,
  onMarkDefaulted,
  onCancel,
}: {
  plan: any;
  onMarkPaid: () => void;
  onMarkDefaulted: () => void;
  onCancel: () => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const installments = useQuery(
    api.paymentPlans.getInstallments,
    expanded ? { paymentPlanId: plan._id } : "skip",
  );

  const paidPct = plan.totalAmount > 0 ? ((plan.paidAmount ?? 0) / plan.totalAmount) * 100 : 0;

  return (
    <div className="rounded border">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-3 text-left text-sm hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <div className="flex-1 grid grid-cols-[1fr_auto_auto_auto] items-center gap-4">
          <div>
            <p className="font-medium">{plan.customerName || plan.customerEmail}</p>
            <p className="text-xs text-muted-foreground">
              {plan.numberOfInstallments} installments · Created{" "}
              {plan._creationTime ? formatDate(plan._creationTime) : "—"}
            </p>
          </div>
          <div className="text-right">
            <p className="font-medium">{formatCurrency(plan.totalAmount ?? 0)}</p>
            <p className="text-xs text-muted-foreground">
              Paid: {formatCurrency(plan.paidAmount ?? 0)}
            </p>
          </div>
          <div className="w-24">
            <div className="h-2 w-full rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-emerald-500"
                style={{ width: `${Math.min(paidPct, 100)}%` }}
              />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground text-center">
              {Math.round(paidPct)}%
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={getPlanStatusBadgeClass(plan.status)}>
              {formatStatusLabel(plan.status)}
            </Badge>
            {plan.overdueCount > 0 ? (
              <Badge variant="destructive">{plan.overdueCount} overdue</Badge>
            ) : null}
          </div>
        </div>
      </button>

      {expanded ? (
        <div className="border-t p-3 space-y-3">
          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground">Original Amount</p>
              <p className="font-medium">{formatCurrency(plan.originalAmount ?? 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Fees Added</p>
              <p className="font-medium">{formatCurrency(plan.feesAmount ?? 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className="font-medium">
                {formatCurrency((plan.totalAmount ?? 0) - (plan.paidAmount ?? 0))}
              </p>
            </div>
          </div>

          {plan.blocksCheckout ? (
            <p className="text-xs text-amber-600">Blocks checkout when overdue</p>
          ) : null}

          {installments ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Installments</p>
              {installments.map((inst: any) => (
                <div
                  key={inst._id}
                  className="flex items-center justify-between rounded border px-3 py-1.5 text-xs"
                >
                  <span>#{inst.installmentNumber}</span>
                  <span>{formatCurrency(inst.amount)}</span>
                  <span>{formatDate(inst.dueDate)}</span>
                  <Badge
                    variant={
                      inst.status === "paid"
                        ? "default"
                        : inst.status === "overdue"
                          ? "destructive"
                          : "secondary"
                    }
                    className="text-xs"
                  >
                    {formatStatusLabel(inst.status)}
                  </Badge>
                </div>
              ))}
            </div>
          ) : null}

          {plan.status === "active" ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={onMarkPaid}>
                Mark Installment Paid
              </Button>
              <Button size="sm" variant="destructive" onClick={onMarkDefaulted}>
                Mark Defaulted
              </Button>
              <Button size="sm" variant="outline" onClick={onCancel}>
                Cancel Plan
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CreatePaymentPlanDialog({ onClose }: { onClose: () => void }) {
  const users = useUsersForImpersonation();
  const report = usePaymentStatusReport();
  const createPlan = useMutation(api.paymentPlans.createPaymentPlan);

  const [step, setStep] = React.useState(1);
  const [userId, setUserId] = React.useState("");
  const [userSearch, setUserSearch] = React.useState("");
  const [selectedPayments, setSelectedPayments] = React.useState<string[]>([]);
  const [fees, setFees] = React.useState(0);
  const [numInstallments, setNumInstallments] = React.useState(3);
  const [installments, setInstallments] = React.useState<{ amount: number; dueDate: string }[]>([]);
  const [blocksCheckout, setBlocksCheckout] = React.useState(true);
  const [notes, setNotes] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const existingPlan = useHasActivePaymentPlan(userId || undefined);

  const filteredUsers = React.useMemo(() => {
    if (!users || !userSearch) return [];
    const q = userSearch.toLowerCase();
    return users
      .filter(
        (u: any) =>
          u.email?.toLowerCase().includes(q) ||
          u.name?.toLowerCase().includes(q) ||
          u.company?.toLowerCase().includes(q),
      )
      .slice(0, 15);
  }, [users, userSearch]);

  const userPayments = React.useMemo(() => {
    if (!report?.buckets || !userId) return [];
    const all: any[] = [];
    for (const bucket of report.buckets) {
      for (const p of bucket.payments ?? []) {
        if (p.userId === userId) all.push(p);
      }
    }
    return all;
  }, [report, userId]);

  const selectedTotal = React.useMemo(() => {
    return userPayments
      .filter((p: any) => selectedPayments.includes(p.scheduledPaymentId))
      .reduce((s: number, p: any) => s + p.amount, 0);
  }, [userPayments, selectedPayments]);

  const planTotal = selectedTotal + fees;

  const generateInstallments = React.useCallback(() => {
    if (numInstallments <= 0 || planTotal <= 0) return;
    const perInst = Math.floor((planTotal / numInstallments) * 100) / 100;
    const remainder = Math.round((planTotal - perInst * numInstallments) * 100) / 100;
    const now = new Date();
    const newInst: { amount: number; dueDate: string }[] = Array.from({ length: numInstallments }, (_, i) => {
      const d = new Date(now);
      d.setMonth(d.getMonth() + i + 1);
      return {
        amount: i === numInstallments - 1 ? perInst + remainder : perInst,
        dueDate: d.toISOString().split("T")[0]!,
      };
    });
    setInstallments(newInst);
  }, [numInstallments, planTotal]);

  const handleCreate = React.useCallback(async () => {
    if (!userId || selectedPayments.length === 0 || installments.length === 0) return;
    const instTotal = installments.reduce((s, i) => s + i.amount, 0);
    if (Math.abs(instTotal - planTotal) > 0.01) {
      toast.error("Installments must total the plan amount");
      return;
    }
    setIsSaving(true);
    try {
      const orderIds = userPayments
        .filter((p: any) => selectedPayments.includes(p.scheduledPaymentId))
        .map((p: any) => p.orderId);
      await createPlan({
        userId: userId as any,
        originalOrderIds: orderIds,
        originalScheduledPaymentIds: selectedPayments as any[],
        originalAmount: selectedTotal,
        feesAmount: fees,
        installments: installments.map((i) => ({
          amount: i.amount,
          dueDate: new Date(i.dueDate).getTime(),
        })),
        blocksCheckout,
        notes: notes || undefined,
      });
      toast.success("Payment plan created");
      onClose();
    } catch (err) {
      toast.error(
        toUserFacingErrorMessage(err, "Couldn't create payment plan. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  }, [userId, selectedPayments, installments, planTotal, selectedTotal, fees, blocksCheckout, notes, userPayments, createPlan, onClose]);

  const selectedUser = users?.find((u: any) => u._id === userId);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Payment Plan - Step {step}/3</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Select a customer"
              : step === 2
                ? "Select overdue payments"
                : "Configure installments"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[300px] space-y-4 py-2">
          {step === 1 ? (
            <div className="space-y-3">
              <Input
                placeholder="Search by email, name, company..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              {userId && selectedUser ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {selectedUser.name || selectedUser.email}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => setUserId("")}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto rounded border">
                  {filteredUsers.map((u: any) => (
                    <button
                      key={u._id}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted"
                      onClick={() => {
                        setUserId(u._id);
                        setUserSearch("");
                      }}
                    >
                      <span className="font-medium">{u.name || u.email}</span>
                      <span className="text-xs text-muted-foreground">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {existingPlan?.hasActivePlan ? (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                  This customer already has an active payment plan. Creating another plan may overlap obligations.
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-3">
              {userPayments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No overdue payments found for this customer
                </p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userPayments.map((p: any) => (
                    <label
                      key={p.scheduledPaymentId}
                      className="flex items-center gap-3 rounded border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPayments.includes(p.scheduledPaymentId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPayments([...selectedPayments, p.scheduledPaymentId]);
                          } else {
                            setSelectedPayments(selectedPayments.filter((id) => id !== p.scheduledPaymentId));
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <span className="font-mono text-xs">{p.orderNumber}</span>
                      <span>{formatCurrency(p.amount)}</span>
                      <Badge variant="outline" className="ml-auto">{p.daysOverdue}d overdue</Badge>
                    </label>
                  ))}
                </div>
              )}
              <div className="grid gap-2">
                <Label>Additional Fees ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={fees || ""}
                  onChange={(e) => setFees(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="rounded border p-3 text-sm">
                <p>Selected: {formatCurrency(selectedTotal)} ({selectedPayments.length} payments)</p>
                <p>Fees: {formatCurrency(fees)}</p>
                <p className="font-semibold">Total: {formatCurrency(planTotal)}</p>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                Plan Total: {formatCurrency(planTotal)}
              </p>
              <div className="flex items-end gap-2">
                <div className="grid gap-1">
                  <Label className="text-xs">Installments</Label>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={numInstallments}
                    onChange={(e) => setNumInstallments(parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={generateInstallments}>
                  Generate Equal Payments
                </Button>
              </div>
              {installments.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {installments.map((inst, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="w-6 text-xs text-muted-foreground">#{idx + 1}</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={inst.amount}
                        onChange={(e) => {
                          const newInst = [...installments];
                          newInst[idx] = { amount: parseFloat(e.target.value) || 0, dueDate: newInst[idx]!.dueDate };
                          setInstallments(newInst);
                        }}
                        className="w-28"
                      />
                      <div className="w-40">
                        <DatePicker
                          value={inst.dueDate}
                          onChange={(value) => {
                            const newInst = [...installments];
                            newInst[idx] = { amount: newInst[idx]!.amount ?? 0, dueDate: value };
                            setInstallments(newInst);
                          }}
                          placeholder="Select due date"
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Total: {formatCurrency(installments.reduce((s, i) => s + i.amount, 0))}
                    {Math.abs(installments.reduce((s, i) => s + i.amount, 0) - planTotal) > 0.01 ? (
                      <span className="text-destructive ml-2">
                        (Mismatch! Should be {formatCurrency(planTotal)})
                      </span>
                    ) : null}
                  </p>
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="blocksCheckout"
                  checked={blocksCheckout}
                  onChange={(e) => setBlocksCheckout(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="blocksCheckout" className="text-sm">
                  Block checkout if installment becomes overdue
                </Label>
              </div>
              <div className="grid gap-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Plan notes..."
                />
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(prev => prev - 1)}>
              Back
            </Button>
          ) : null}
          {step < 3 ? (
            <Button
              onClick={() => setStep(prev => prev + 1)}
              disabled={
                (step === 1 && !userId) ||
                (step === 2 && selectedPayments.length === 0)
              }
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={() => void handleCreate()}
              disabled={isSaving || installments.length === 0}
            >
              {isSaving ? "Creating..." : "Create Plan"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({ plan, onClose }: { plan: any; onClose: () => void }) {
  const installments = useQuery(api.paymentPlans.getInstallments, {
    paymentPlanId: plan._id,
  });
  const markPaid = useMutation(api.paymentPlans.markInstallmentPaidManually);

  const [selectedInstallment, setSelectedInstallment] = React.useState("");
  const [paymentMethod, setPaymentMethod] = React.useState<"cash" | "check" | "wire" | "other">("check");
  const [reference, setReference] = React.useState("");
  const [note, setNote] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const unpaid = React.useMemo(
    () => installments?.filter((i: any) => i.status !== "paid") ?? [],
    [installments],
  );

  const handleSave = React.useCallback(async () => {
    if (!selectedInstallment) return;
    setIsSaving(true);
    try {
      await markPaid({
        installmentId: selectedInstallment as any,
        paymentMethod,
        reference: reference || undefined,
        note: note || undefined,
      });
      toast.success("Installment marked as paid");
      onClose();
    } catch (err) {
      toast.error(
        toUserFacingErrorMessage(err, "Couldn't mark installment as paid. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  }, [selectedInstallment, paymentMethod, reference, note, markPaid, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark Installment Paid</DialogTitle>
          <DialogDescription>{plan.customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Select Installment</Label>
            <Select value={selectedInstallment} onValueChange={setSelectedInstallment}>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {unpaid.map((inst: any) => (
                  <SelectItem key={inst._id} value={inst._id}>
                    #{inst.installmentNumber} - {formatCurrency(inst.amount)} (due{" "}
                    {formatDate(inst.dueDate)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Payment Method</Label>
            <div className="flex gap-2">
              {(["check", "wire", "cash", "other"] as const).map((m) => (
                <Button
                  key={m}
                  variant={paymentMethod === m ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPaymentMethod(m)}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Reference # (optional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Check #, wire code, etc."
            />
          </div>
          <div className="grid gap-2">
            <Label>Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !selectedInstallment}
          >
            {isSaving ? "Saving..." : "Mark Paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
